import * as React from 'react';
import dayjs, {Dayjs} from 'dayjs';
import Badge from '@mui/material/Badge';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {PickersDay, PickersDayProps} from '@mui/x-date-pickers/PickersDay';
import {DateCalendar} from '@mui/x-date-pickers/DateCalendar';
import {CustomDateAdapter} from './CustomDateAdapter';
import "dayjs/locale/lt";
import {Dispatch, useRef, useState} from "react";

function fetchCalendar(date: Dayjs, {signal}: {
    signal: AbortSignal
}, address: string, trashType: string, containerSize: string, setShowLoader: any) {
    return new Promise<{ daysToHighlight: any[] }>((resolve, reject) => {
        const daysToHighlight = null;
        const params = {
            trashType: trashType,
            containerSize: containerSize,
            address: address,
            date: date.format('YYYY-MM-DD')
        };
        const options = {
            method: 'POST',
            body: JSON.stringify(params)
        };

        if (address.length > 0) {
            setShowLoader('show');
            fetch(
                "calendar", options)
                .then((res) => res.json())
                .then((daysToHighlight) => {
                    resolve({daysToHighlight})
                    setShowLoader('hide');
                });
        }
    });
}

function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: any[] }) {
    const {highlightedDays = [], day, outsideCurrentMonth, ...other} = props;

    const isSelected = highlightedDays.hasOwnProperty(props.day.format('YYYY-MM-DD'));

    let availability = 'available';

    if (isSelected && highlightedDays[props.day.format('YYYY-MM-DD')].availableAmount < 3) {
        availability = 'low-availability';
    }

    if (isSelected && highlightedDays[props.day.format('YYYY-MM-DD')].availableAmount == 0) {
        availability = 'not-available';
    }

    return (
        <Badge
            key={props.day.toString()}
            overlap="circular"
            badgeContent={isSelected ? highlightedDays[props.day.format('YYYY-MM-DD')].price : undefined}
            max={1000}
        >
            <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} className={availability}/>
        </Badge>
    );
}

type appProps = {
    address: string,
    trashType: string,
    containerSize: string,
    disabled: boolean,
    setSelectedDate: any,
    setSelectedPrice: any,
    setSelectedContainer: any,
    setContainerId: any,
    withValues: string,
    maxYearToSelect: number,
    translations: any,
    setShowLoader: Dispatch<any>,
    beforeForm: any
};

export const Calendar = (
    {
        address,
        trashType,
        containerSize,
        disabled,
        setSelectedDate,
        setSelectedPrice,
        setSelectedContainer,
        setContainerId,
        withValues,
        maxYearToSelect,
        translations,
        setShowLoader,
        beforeForm
    }: appProps
) => {
    const requestAbortController = React.useRef<AbortController | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [highlightedDays, setHighlightedDays] = React.useState(['2023-09-03', 2, 3]);
    const [selectedPriceInner, setSelectedPriceInner] = useState('--');
    const fetchHighlightedDays = (date: Dayjs) => {
        const controller = new AbortController();

        if (trashType != '' && containerSize != '') {
            fetchCalendar(date, {
                    signal: controller.signal
                },
                address,
                trashType,
                containerSize,
                setShowLoader
            )
                .then(({daysToHighlight}) => {
                    setHighlightedDays(daysToHighlight);
                    setIsLoading(false);

                    setContainerId(daysToHighlight[Object.keys(daysToHighlight)[0]].containerId);
                })
                .catch((error) => {
                    // ignore the error if it's caused by `controller.abort`
                    if (error.name !== 'AbortError') {
                        throw error;
                    }
                });
        }
        requestAbortController.current = controller;
    };

    const isValidYear = (date: Dayjs) => {
        const year = date.year();

        return !(year >= new Date().getFullYear() && year < maxYearToSelect);
    };

    const isWeekend = (date: Dayjs) => {
        const day = date.day();
        const isSelected = highlightedDays.hasOwnProperty(date.format('YYYY-MM-DD'));

        if (isSelected
            && highlightedDays[date.format('YYYY-MM-DD')].availableAmount == 0
            && highlightedDays[date.format('YYYY-MM-DD')].price != 'Klausti'
        ) {
            return true;
        }

        if (!highlightedDays.hasOwnProperty(date.format('YYYY-MM-DD'))) {
            return true;
        }

        return day === 0 || day === 6;
    };

    const handleMonthChange = (date: Dayjs) => {
        if (requestAbortController.current) {
            requestAbortController.current.abort();
        }

        setIsLoading(false);
        setHighlightedDays([]);
        fetchHighlightedDays(date);
    };

    const handleSelection = (date: Dayjs) => {
        setSelectedDate(date.format('YYYY-MM-DD'))
        setSelectedPrice(highlightedDays.hasOwnProperty(date.format('YYYY-MM-DD')) ? highlightedDays[date.format('YYYY-MM-DD')].price : '--')
        setSelectedPriceInner(highlightedDays.hasOwnProperty(date.format('YYYY-MM-DD')) ? highlightedDays[date.format('YYYY-MM-DD')].price : '--')
        setSelectedContainer(highlightedDays.hasOwnProperty(date.format('YYYY-MM-DD')) ? highlightedDays[date.format('YYYY-MM-DD')].containerId : '--')
        beforeForm.current?.scrollIntoView({behavior: 'smooth'});
    }

    React.useEffect(() => {
        fetchHighlightedDays(dayjs(new Date()));
        return () => requestAbortController.current?.abort();
    }, [withValues]);


    const [touchStart, setTouchStart] = useState(null)
    const [touchEnd, setTouchEnd] = useState(null)

// the required distance between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 50

    const onTouchStart = (e) => {
        setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
        setTouchStart(e.targetTouches[0].clientX)
    }

    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX)

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return
        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance
        if (isLeftSwipe) {
            document.getElementsByClassName('css-1nkg345-MuiButtonBase-root-MuiIconButton-root-MuiPickersArrowSwitcher-button')['0'].click();
        }

        if (isRightSwipe) {
            document.getElementsByClassName('css-kg9q0s-MuiButtonBase-root-MuiIconButton-root-MuiPickersArrowSwitcher-button')['0'].click();
        }
        // add your conditional logic here
    }

    return (
        <LocalizationProvider dateAdapter={CustomDateAdapter} adapterLocale="lt">
            <DateCalendar
                loading={isLoading}
                onMonthChange={handleMonthChange}
                slots={{
                    day: ServerDay,
                }}
                slotProps={{
                    day: {
                        highlightedDays,
                    } as any,
                }}
                disabled={disabled}
                disablePast
                shouldDisableDate={isWeekend}
                shouldDisableYear={isValidYear}
                onChange={handleSelection}
                showDaysOutsideCurrentMonth
                onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
            />
            <>
                <span className="inner-price">
                    <span>{translations.price}</span><br/>
                    <span><strong>{selectedPriceInner}</strong></span>
                </span>
                <ul id={'containersExplanation'}>
                    <li className={'not-available'}>{translations.containers_not_available}</li>
                    <li className={'available'}>{translations.containers_available}</li>
                    <li className={'low-availability'}>{translations.containers_low_availability}</li>
                </ul>
            </>
        </LocalizationProvider>
    );
}
